import {
  Box,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { DeleteOutline } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

import type { ClientMember } from '../clientsUtils';
import { formatClientDate } from '../clientsUtils';
import { getClientsPageTokens } from '../getClientsPageTokens';
import { MEMBER_ROWS_PER_PAGE_OPTIONS } from '../clientsTokens';

export interface MemberTableProps {
  filteredMembersCount: number;
  loadingMembers: boolean;
  memberPage: number;
  memberRowsPerPage: number;
  membersCount: number;
  onMemberPageChange: (page: number) => void;
  onMemberRowsPerPageChange: (rowsPerPage: number) => void;
  onRemoveMember: (member: ClientMember) => void;
  pagedMembers: ClientMember[];
  saving: boolean;
  selectedClientName: string | null;
}

export function MemberTable({
  filteredMembersCount,
  loadingMembers,
  memberPage,
  memberRowsPerPage,
  membersCount,
  onMemberPageChange,
  onMemberRowsPerPageChange,
  onRemoveMember,
  pagedMembers,
  saving,
  selectedClientName,
}: MemberTableProps): JSX.Element {
  const theme = useTheme();
  const tokens = getClientsPageTokens(theme);

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <TableContainer sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: tokens.filterLabelColor, fontWeight: 600, borderColor: tokens.sectionSurfaceBorder }}>
                Email
              </TableCell>
              <TableCell sx={{ color: tokens.filterLabelColor, fontWeight: 600, borderColor: tokens.sectionSurfaceBorder }}>
                Account
              </TableCell>
              <TableCell sx={{ color: tokens.filterLabelColor, fontWeight: 600, borderColor: tokens.sectionSurfaceBorder }}>
                Added
              </TableCell>
              <TableCell
                align="right"
                sx={{ color: tokens.filterLabelColor, fontWeight: 600, borderColor: tokens.sectionSurfaceBorder }}
              >
                Remove
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedMembers.map((member) => (
              <TableRow
                key={member.externalUuid}
                hover
                sx={{ '&:last-child td': { borderColor: tokens.sectionSurfaceBorder } }}
              >
                <TableCell sx={{ borderColor: tokens.sectionSurfaceBorder }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: tokens.pageHeadingColor }}>
                    {member.email}
                  </Typography>
                  {member.name ? (
                    <Typography variant="caption" sx={{ color: tokens.bodySecondaryColor }}>
                      {member.name}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell sx={{ borderColor: tokens.sectionSurfaceBorder }}>
                  <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Chip
                      label={member.accountType}
                      size="small"
                      sx={{
                        height: 24,
                        bgcolor: tokens.tagChipBg,
                        color: tokens.tagChipColor,
                        fontWeight: 500,
                      }}
                    />
                    {member.isPoweruser ? (
                      <Chip
                        label="poweruser"
                        size="small"
                        sx={{
                          height: 24,
                          bgcolor: tokens.filterPillActiveBg,
                          color: tokens.filterPillActiveColor,
                          fontWeight: 500,
                        }}
                      />
                    ) : null}
                  </Box>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', borderColor: tokens.sectionSurfaceBorder, color: tokens.bodySecondaryColor }}>
                  {formatClientDate(member.createdAt)}
                </TableCell>
                <TableCell align="right" sx={{ borderColor: tokens.sectionSurfaceBorder }}>
                  <IconButton
                    aria-label={`Remove ${member.email}`}
                    size="small"
                    disabled={saving}
                    onClick={() => onRemoveMember(member)}
                    sx={{ color: tokens.filterLabelColor }}
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {!loadingMembers && filteredMembersCount === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 5, borderColor: tokens.sectionSurfaceBorder }}>
                  <Typography variant="body2" sx={{ color: tokens.bodySecondaryColor }}>
                    {!selectedClientName
                      ? 'Select a client to manage members.'
                      : membersCount === 0
                        ? 'No members found.'
                        : 'No members match your search.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredMembersCount > 0 ? (
        <TablePagination
          component="div"
          count={filteredMembersCount}
          page={memberPage}
          onPageChange={(_event, newPage) => onMemberPageChange(newPage)}
          rowsPerPage={memberRowsPerPage}
          onRowsPerPageChange={(event) => {
            onMemberRowsPerPageChange(parseInt(event.target.value, 10));
            onMemberPageChange(0);
          }}
          rowsPerPageOptions={[...MEMBER_ROWS_PER_PAGE_OPTIONS]}
          sx={{
            flexShrink: 0,
            color: tokens.bodySecondaryColor,
            borderTop: `1px solid ${tokens.sectionSurfaceBorder}`,
          }}
        />
      ) : null}
    </Box>
  );
}
